import React from "react";
import { motion } from "framer-motion";
import { Compass, Brain, Sparkles, Globe2 } from "lucide-react";
import SEO from "@/components/site/SEO";
import SectionHeading from "@/components/site/SectionHeading";
import FinalCTA from "@/components/site/FinalCTA";
import { TALENT_DISCIPLINES, TALENT_VALUES, IMAGES } from "@/data/content";

const valueIcons = [Compass, Brain, Sparkles, Globe2];

export default function TalentPage({ onStartProject }) {
    return (
        <div data-testid="page-talent">
            <SEO
                title="Talent Network — Senior, Curated, AI-Augmented Specialists"
                description="A curated network of senior strategists, designers, AI engineers, and operators — vetted, briefed by AI, and orchestrated against a clear outcome. Not a marketplace."
                path="/talent"
                keywords="creative talent network, AI engineering talent, fractional creative director, senior strategist, design talent, marketing operators"
            />
            <header className="relative pt-32 lg:pt-40 pb-20 overflow-hidden">
                <div className="absolute inset-0 hero-radial pointer-events-none" />
                <div className="absolute inset-0 dot-grid-fine opacity-50 pointer-events-none [mask-image:radial-gradient(ellipse_at_top,#000_30%,transparent_75%)]" />
                <div className="relative mx-auto max-w-7xl container-px grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <SectionHeading
                            eyebrow="Talent Network"
                            title={
                                <>
                                    A curated expert network —{" "}
                                    <span className="italic text-[hsl(var(--text-muted))]">not a marketplace.</span>
                                </>
                            }
                            subtitle="We assemble small, opinionated teams of senior strategists, creative directors, designers, and engineers — matched to the outcome, briefed by AI, supervised by people whose taste is the moat."
                        />
                        <div className="mt-8 flex flex-wrap items-center gap-3 text-[12.5px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-muted))]">
                            <span className="inline-flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--blue))]" />
                                Curated specialists
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))]" />
                                Human judgment
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--blue))]" />
                                Creative intelligence
                            </span>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="relative aspect-square rounded-3xl border border-black/[0.08] bg-white overflow-hidden shadow-deep"
                        >
                            <img
                                src={IMAGES.talentNetwork}
                                alt="Curated expert network"
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="eager"
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(255,255,255,0) 50%, rgba(255,255,255,0.55) 100%)",
                                }}
                            />
                            <div className="absolute bottom-5 left-5 right-5 rounded-2xl glass-strong p-4">
                                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                    Operating model
                                </div>
                                <div className="font-display text-xl text-[hsl(var(--text))] mt-1">
                                    AI accelerates. Humans elevate.
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </header>

            <section className="mx-auto max-w-7xl container-px py-20 lg:py-24">
                <SectionHeading
                    eyebrow="Disciplines"
                    title="Senior craft across every layer of the stack."
                    subtitle="Hand-picked specialists with shipped outcomes — not portfolios full of pitches."
                    className="mb-12"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {TALENT_DISCIPLINES.map((d, i) => (
                        <motion.div
                            key={d.title}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{
                                duration: 0.5,
                                delay: i * 0.04,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="rounded-2xl border border-black/[0.07] bg-white p-6 hover:border-black/15 hover:shadow-soft transition-all duration-300"
                            data-testid="talent-discipline"
                        >
                            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                Discipline
                            </div>
                            <h4 className="mt-1.5 font-display text-2xl text-[hsl(var(--text))]">
                                {d.title}
                            </h4>
                            <p className="mt-2 text-[14px] text-[hsl(var(--text-muted))] leading-relaxed">
                                {d.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-7xl container-px py-20 lg:py-24 border-t border-black/[0.06]">
                <SectionHeading
                    eyebrow="Operating principles"
                    title={
                        <>
                            Curation, judgment, craft —{" "}
                            <span className="italic text-[hsl(var(--text-muted))]">in that order.</span>
                        </>
                    }
                    subtitle="How we run a talent network that behaves like a platform, not a marketplace."
                    className="mb-12"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {TALENT_VALUES.map((v, i) => {
                        const Icon = valueIcons[i % valueIcons.length];
                        return (
                            <motion.div
                                key={v.title}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-10% 0px" }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.05,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="flex items-start gap-4 rounded-2xl border border-black/[0.07] bg-white p-6"
                            >
                                <span className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(187,118,33,0.10)] ring-1 ring-[rgba(187,118,33,0.35)] text-[hsl(var(--gold))]">
                                    <Icon className="h-4 w-4" />
                                </span>
                                <div>
                                    <h5 className="font-display text-xl text-[hsl(var(--text))]">
                                        {v.title}
                                    </h5>
                                    <p className="text-[14px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">
                                        {v.description}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            <FinalCTA onStartProject={onStartProject} />
        </div>
    );
}
